import { useParams } from "react-router-dom";
import Link from "next/link";
import { ArrowLeft, Check } from "lucide-react";
import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";
import CaseStudyCard from "@/components/case-studies/CaseStudyCard";
import IllustrativeBadge from "@/components/case-studies/IllustrativeBadge";
import FinalCta from "@/components/landing-page/home/FinalCta";
import Reveal from "@/components/ui/Reveal";
import { CASE_STUDIES, getCaseStudy } from "@/lib/data/case-studies";

export default function CaseStudyPage() {
  const { slug } = useParams<{ slug: string }>();
  const study = slug ? getCaseStudy(slug) : null;

  if (!study) {
    return (
      <LandingPageShell>
        <div className="min-h-[50vh] flex flex-col items-center justify-center text-center p-8">
          <h1 className="text-2xl font-bold mb-4">Case study not found</h1>
          <Link href="/case-studies" className="text-mkt-primary underline">
            Return to all case studies
          </Link>
        </div>
      </LandingPageShell>
    );
  }

  const related = CASE_STUDIES.filter((other) => other.slug !== study.slug)
    .sort((a, b) => Number(b.category === study.category) - Number(a.category === study.category))
    .slice(0, 3);

  return (
    <LandingPageShell>
      <section className="bg-white px-margin-mobile md:px-margin-desktop pt-size-lg pb-size-xl">
        <Reveal className="max-w-[1440px] mx-auto">
          <Link
            href="/case-studies"
            className="group inline-flex items-center gap-1.5 font-label-md text-label-md font-bold text-mkt-primary mb-size-lg"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            All case studies
          </Link>
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-size-sm mb-size-md">
              <span className="font-label-md text-label-md uppercase tracking-widest text-mkt-primary">{study.industry}</span>
              <span aria-hidden="true" className="text-mkt-outline-variant">·</span>
              <span className="font-label-md text-label-md text-mkt-on-surface-variant">{study.category}</span>
              {study.illustrative ? <IllustrativeBadge /> : null}
            </div>
            <h1 className="font-display-lg text-[clamp(30px,4vw,56px)] leading-[1.1] tracking-[-0.02em] font-bold text-mkt-inarax-black text-balance">
              {study.title}
            </h1>
            <p className="mt-size-md font-body-lg text-body-lg text-mkt-on-surface-variant">
              <span className="font-semibold text-mkt-on-surface">{study.client}.</span> {study.summary}
            </p>
          </div>
        </Reveal>
      </section>

      <section aria-label="Results at a glance" className="bg-mkt-inarax-black px-margin-mobile md:px-margin-desktop py-size-lg">
        <dl className="max-w-[1440px] mx-auto grid grid-cols-1 sm:grid-cols-3 gap-gutter">
          {study.metrics.map((metric) => (
            <div key={metric.label} className="flex flex-col-reverse gap-1 border-l-2 border-mkt-primary pl-size-md">
              <dt className="font-body-md text-body-md text-white">{metric.label}</dt>
              <dd className="font-display-lg text-[clamp(32px,3.4vw,48px)] font-bold leading-none text-white">{metric.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="bg-white px-margin-mobile md:px-margin-desktop py-size-xl">
        <div className="max-w-4xl mx-auto flex flex-col gap-size-xl">
          <Reveal>
            <h2 className="font-headline-lg text-headline-md md:text-headline-lg text-mkt-inarax-black mb-size-md">The challenge</h2>
            <p className="font-body-lg text-body-lg text-mkt-on-surface-variant">{study.challenge}</p>
          </Reveal>

          <Reveal>
            <h2 className="font-headline-lg text-headline-md md:text-headline-lg text-mkt-inarax-black mb-size-md">The approach</h2>
            <ol className="list-none pl-0 ml-0 flex flex-col gap-size-md">
              {study.approach.map(({ step, detail }, index) => (
                <li key={step} className="flex gap-size-md">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-mkt-primary-container text-mkt-on-primary font-bold">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-headline-md text-headline-sm text-mkt-on-surface">{step}</h3>
                    <p className="font-body-md text-body-md text-mkt-on-surface-variant">{detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>

          <Reveal>
            <h2 className="font-headline-lg text-headline-md md:text-headline-lg text-mkt-inarax-black mb-size-md">The results</h2>
            <ul className="list-none pl-0 ml-0 flex flex-col gap-size-sm">
              {study.results.map((result) => (
                <li key={result} className="flex items-start gap-size-sm font-body-lg text-body-lg text-mkt-on-surface">
                  <Check aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-mkt-primary" />
                  {result}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section aria-labelledby="related-heading" className="bg-mkt-surface px-margin-mobile md:px-margin-desktop py-size-xl">
        <div className="max-w-[1440px] mx-auto">
          <h2 id="related-heading" className="font-headline-lg text-headline-md md:text-headline-lg text-mkt-inarax-black mb-size-lg">
            More case studies
          </h2>
          <ul className="list-none pl-0 ml-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-gutter">
            {related.map((other) => (
              <li key={other.slug}>
                <CaseStudyCard study={other} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <FinalCta />
    </LandingPageShell>
  );
}
